/**
 * useLeadsQuery.ts — fetch + Previous/Next state for the Leads list.
 *
 * Owns the request lifecycle for `POST /api/leads/search`: a fresh filter `load` (page 1),
 * then `goNext` / `goPrev` over the opaque keyset `next_cursor`. The hook keeps a
 * CURSOR STACK (`cursors[i]` = the cursor that fetched page i+1; page 1 = null), so
 * Previous simply re-requests the earlier cursor — no offsets, no page-number
 * assumptions. Rows are REPLACED per page, never appended.
 *
 * Race-safety mirrors HealthPage: a monotonic request id means only the LATEST
 * request may commit, and a response resolving after unmount is a no-op (no
 * AbortController — it is cross-realm-flaky under jsdom+undici).
 *
 * Failure posture (spec §Error Handling): a 401/403 → `unauthorized`; the BFF's 502
 * or any network/5xx → `error` on a fresh load. A failed page move keeps the rows
 * and the stack as they were and flags `errored`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient, LeadsQuery } from '../../api/client';
import { ApiError, UnauthorizedError } from '../../api/client';
import type { LeadListItem } from '../../api/types';

/** Fixed page size (spec F1); the BFF default is 50, so every request sets it. */
export const LEADS_PAGE_SIZE = 25;

export type LeadsPhase = 'loading' | 'ready' | 'unauthorized' | 'error';

export interface LeadsQueryState {
  items: LeadListItem[];
  nextCursor: string | null;
  /** Exact total from the envelope (ADR-089); 0 before the first page. */
  total: number;
  generatedAt: string | null;
  phase: LeadsPhase;
  /** 1-based page number (cursor-stack depth). */
  page: number;
  /** Set when a load failed (transport / 502 / 5xx); drives the error banner. */
  errored: boolean;
  /** The server's fixed 422 message for a rejected search term (never the term); else null. */
  searchError: string | null;
  /** Run a fresh first-page query (replaces the rows, resets to page 1). */
  load: (query: LeadsQuery) => Promise<void>;
  /** Next keyset page; no-op when there is no `next_cursor`. */
  goNext: () => Promise<void>;
  /** Previous page; no-op on page 1. */
  goPrev: () => Promise<void>;
}

export const useLeadsQuery = (apiClient: ApiClient): LeadsQueryState => {
  const [items, setItems] = useState<LeadListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [phase, setPhase] = useState<LeadsPhase>('loading');
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [errored, setErrored] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  const requestSeq = useRef(0);
  // The active filter set (sans cursor), so paging can advance from it.
  const lastQueryRef = useRef<LeadsQuery>({});

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Fetch the page at `stack[stack.length - 1]`; commit `stack` only on success.
  const run = useCallback(
    async (query: LeadsQuery, stack: Array<string | null>, { fresh }: { fresh: boolean }) => {
      requestSeq.current += 1;
      const seq = requestSeq.current;
      const isCurrent = () => mountedRef.current && seq === requestSeq.current;
      setPhase('loading');
      try {
        const envelope = await apiClient.searchLeads({
          ...query,
          cursor: stack[stack.length - 1] ?? null,
        });
        if (!isCurrent()) return;
        setItems(envelope.items);
        setNextCursor(envelope.next_cursor);
        setTotal(envelope.total);
        setGeneratedAt(envelope.generated_at);
        setCursors(stack);
        setErrored(false);
        setSearchError(null);
        setPhase('ready');
      } catch (error) {
        if (!isCurrent()) return;
        if (error instanceof UnauthorizedError) {
          setPhase('unauthorized');
          return;
        }
        // A rejected search term: keep the last rows, show the server's fixed message.
        if (error instanceof ApiError && error.status === 422) {
          setSearchError(error.detail ?? '');
          setPhase('ready');
          return;
        }
        // BFF 502 (source unavailable) / network / 5xx.
        setErrored(true);
        if (fresh) {
          setItems([]);
          setNextCursor(null);
          setCursors([null]);
          setPhase('error');
        } else {
          setPhase('ready');
        }
      }
    },
    [apiClient],
  );

  const load = useCallback(
    async (query: LeadsQuery) => {
      lastQueryRef.current = query;
      await run(query, [null], { fresh: true });
    },
    [run],
  );

  const goNext = useCallback(async () => {
    if (nextCursor == null) return;
    await run(lastQueryRef.current, [...cursors, nextCursor], { fresh: false });
  }, [cursors, nextCursor, run]);

  const goPrev = useCallback(async () => {
    if (cursors.length < 2) return;
    await run(lastQueryRef.current, cursors.slice(0, -1), { fresh: false });
  }, [cursors, run]);

  return {
    items,
    nextCursor,
    total,
    generatedAt,
    phase,
    page: cursors.length,
    errored,
    searchError,
    load,
    goNext,
    goPrev,
  };
};
